/**
 * Newsletter Subscriber Management
 * Add, remove, bulk-import, and export subscribers.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Mail, Plus, Trash2, Download, Upload, RefreshCw,
  CheckCircle, AlertCircle, Users, UserMinus, Search,
  X, Send,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function Tag({ children, color = '#a68eff' }) {
  return (
    <span className="text-[9px] px-1.5 py-0.5 rounded-full font-bold"
      style={{ background: `${color}18`, color, border: `1px solid ${color}30` }}>
      {children}
    </span>
  );
}

export default function NewsletterSubscribers() {
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, unsubscribed: 0 });
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removing, setRemoving] = useState({});
  const [search, setSearch] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newName, setNewName] = useState('');
  const [addError, setAddError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [importing, setImporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/newsletter/subscribers`);
      setSubscribers(data.subscribers || []);
      setStats({ total: data.total, active: data.active, unsubscribed: data.unsubscribed });
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSubscriber = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setAdding(true);
    setAddError('');
    setAddSuccess('');
    try {
      const { data } = await axios.post(`${API}/api/newsletter/subscribers`, {
        email: newEmail.trim(),
        name: newName.trim(),
      });
      setAddSuccess(data.action === 'already_subscribed' ? 'Already subscribed!' : `${newEmail} added!`);
      setNewEmail('');
      setNewName('');
      load();
      setTimeout(() => setAddSuccess(''), 3000);
    } catch (e) {
      setAddError(e?.response?.data?.detail || 'Failed to add subscriber');
    } finally {
      setAdding(false);
    }
  };

  const removeSubscriber = async (email) => {
    if (!window.confirm(`Unsubscribe ${email}?`)) return;
    setRemoving(r => ({ ...r, [email]: true }));
    try {
      await axios.delete(`${API}/api/newsletter/subscribers/${encodeURIComponent(email)}`);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Failed');
    } finally {
      setRemoving(r => ({ ...r, [email]: false }));
    }
  };

  const exportCSV = async () => {
    const { data } = await axios.get(`${API}/api/newsletter/subscribers/export`);
    const blob = new Blob([data], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const bulkImport = async () => {
    if (!importText.trim()) return;
    setImporting(true);
    try {
      const lines = importText.split('\n').map(l => l.trim()).filter(Boolean);
      const parsed = lines.map(line => {
        const [email, name] = line.split(',');
        return { email: (email || '').trim(), name: (name || '').trim() };
      }).filter(r => r.email.includes('@'));

      const { data } = await axios.post(`${API}/api/newsletter/subscribers/bulk-import`, {
        subscribers: parsed,
      });
      alert(`Import complete: ${data.added} added, ${data.skipped} skipped`);
      setImportText('');
      setShowImport(false);
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
    }
  };

  const filtered = subscribers.filter(s => {
    const q = search.toLowerCase();
    return !q || s.email.includes(q) || (s.name || '').toLowerCase().includes(q);
  });

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ background: 'var(--llmc-bg)' }}>
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4" style={{ borderBottom: '1px solid var(--llmc-border)', background: 'rgba(10,10,20,0.97)' }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center"
              style={{ background: 'linear-gradient(135deg, rgba(61,255,160,0.2), rgba(0,212,184,0.1))', border: '1px solid rgba(61,255,160,0.3)' }}>
              <Mail size={20} style={{ color: '#3dffa0' }} />
            </div>
            <div>
              <div className="font-black text-white text-sm">Newsletter Subscribers</div>
              <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
                Manage your subscriber list · Export CSV · Bulk import
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowImport(s => !s)}
              data-testid="bulk-import-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--llmc-border)', color: 'var(--llmc-text-muted)' }}>
              <Upload size={12} /> Import CSV
            </button>
            <button onClick={exportCSV}
              data-testid="export-csv-btn"
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all hover:opacity-80"
              style={{ background: 'rgba(61,255,160,0.08)', border: '1px solid rgba(61,255,160,0.25)', color: '#3dffa0' }}>
              <Download size={12} /> Export CSV
            </button>
            <button onClick={load} disabled={loading}
              data-testid="refresh-subscribers-btn"
              className="p-1.5 rounded-lg transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid var(--llmc-border)' }}>
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} style={{ color: 'var(--llmc-text-muted)' }} />
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 space-y-5">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-3" data-testid="subscriber-stats">
          {[
            { label: 'Total Subscribers', value: stats.total, color: '#a68eff', icon: Users },
            { label: 'Active', value: stats.active, color: '#3dffa0', icon: CheckCircle },
            { label: 'Unsubscribed', value: stats.unsubscribed, color: '#ff5370', icon: UserMinus },
          ].map(({ label, value, color, icon: Icon }) => (
            <div key={label} className="rounded-2xl p-4 flex items-center gap-3.5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid var(--llmc-border)' }}>
              <div className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ background: `${color}12`, border: `1px solid ${color}20` }}>
                <Icon size={16} style={{ color }} />
              </div>
              <div>
                <div className="text-2xl font-black text-white">{value}</div>
                <div className="text-xs" style={{ color: 'var(--llmc-text-muted)' }}>{label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Add Subscriber */}
        <div className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--llmc-border)' }}>
          <div className="text-xs font-bold text-white mb-3 flex items-center gap-2">
            <Plus size={13} style={{ color: '#3dffa0' }} /> Add Subscriber
          </div>
          <form onSubmit={addSubscriber} className="flex gap-2">
            <input
              type="email"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              placeholder="Email address"
              required
              data-testid="subscriber-email-input"
              className="flex-1 llmc-input px-3 py-2 text-xs"
            />
            <input
              type="text"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              placeholder="Name (optional)"
              data-testid="subscriber-name-input"
              className="w-40 llmc-input px-3 py-2 text-xs"
            />
            <button type="submit" disabled={adding || !newEmail.trim()}
              data-testid="add-subscriber-btn"
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
              style={{ background: 'rgba(61,255,160,0.12)', border: '1px solid rgba(61,255,160,0.3)', color: '#3dffa0' }}>
              {adding ? <RefreshCw size={11} className="animate-spin" /> : <Plus size={11} />}
              Add
            </button>
          </form>
          {addSuccess && <div className="mt-1.5 text-xs" style={{ color: '#3dffa0' }}>{addSuccess}</div>}
          {addError && <div className="mt-1.5 text-xs" style={{ color: '#ff5370' }}>{addError}</div>}
        </div>

        {/* Bulk Import */}
        {showImport && (
          <div className="rounded-2xl p-5"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,165,0,0.2)' }}>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs font-bold text-white flex items-center gap-2">
                <Upload size={13} style={{ color: '#f97316' }} /> Bulk Import (CSV format: email,name)
              </div>
              <button onClick={() => setShowImport(false)}>
                <X size={14} style={{ color: 'var(--llmc-text-muted)' }} />
              </button>
            </div>
            <textarea
              value={importText}
              onChange={e => setImportText(e.target.value)}
              placeholder={`alice@example.com,Alice Smith\nbob@example.com,Bob Jones\ncharlie@example.com`}
              rows={5}
              data-testid="bulk-import-textarea"
              className="w-full llmc-input px-3 py-2 text-xs font-mono resize-none"
            />
            <div className="flex gap-2 mt-2.5">
              <button onClick={bulkImport} disabled={importing || !importText.trim()}
                data-testid="confirm-bulk-import-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold transition-all hover:opacity-80 disabled:opacity-40"
                style={{ background: 'rgba(249,115,22,0.12)', border: '1px solid rgba(249,115,22,0.3)', color: '#f97316' }}>
                {importing ? <RefreshCw size={11} className="animate-spin" /> : <Send size={11} />}
                {importing ? 'Importing...' : 'Import'}
              </button>
              <div className="text-[10px] my-auto" style={{ color: 'var(--llmc-text-muted)' }}>
                One entry per line · email,name format · duplicates are skipped
              </div>
            </div>
          </div>
        )}

        {/* Subscriber List */}
        <div className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--llmc-border)' }}>
          <div className="flex items-center justify-between px-5 py-3"
            style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--llmc-border)' }}>
            <div className="text-xs font-bold text-white">{filtered.length} subscribers</div>
            <div className="relative">
              <Search size={11} className="absolute left-2.5 top-1/2 -translate-y-1/2" style={{ color: 'rgba(255,255,255,0.3)' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search..."
                data-testid="subscriber-search"
                className="llmc-input pl-7 pr-3 py-1.5 text-xs w-48"
              />
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw size={20} className="animate-spin" style={{ color: '#a68eff' }} />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-xs" style={{ color: 'var(--llmc-text-muted)' }}>
              {search ? 'No matches found' : 'No subscribers yet. Add your first subscriber above.'}
            </div>
          ) : (
            <div>
              {filtered.map((sub) => (
                <div key={sub.id || sub.email}
                  data-testid="subscriber-row"
                  className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.02] transition-colors"
                  style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold uppercase"
                    style={{ background: 'rgba(124,94,250,0.15)', color: '#a68eff' }}>
                    {(sub.name?.[0] || sub.email[0]).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    {sub.name && <div className="text-xs font-semibold text-white">{sub.name}</div>}
                    <div className="text-xs" style={{ color: sub.name ? 'var(--llmc-text-muted)' : 'rgba(255,255,255,0.7)' }}>{sub.email}</div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {(sub.tags || []).map((t, i) => <Tag key={i}>{t}</Tag>)}
                    <Tag color={sub.status === 'active' ? '#3dffa0' : '#ff5370'}>
                      {sub.status || 'active'}
                    </Tag>
                    <div className="text-[9px]" style={{ color: 'rgba(255,255,255,0.2)' }}>
                      {sub.created_at ? new Date(sub.created_at).toLocaleDateString() : ''}
                    </div>
                    {sub.status !== 'unsubscribed' && (
                      <button
                        onClick={() => removeSubscriber(sub.email)}
                        disabled={removing[sub.email]}
                        data-testid={`remove-subscriber-${sub.email}`}
                        className="p-1 rounded-lg transition-all hover:opacity-80 disabled:opacity-40"
                        style={{ color: 'rgba(255,83,112,0.5)' }}>
                        {removing[sub.email] ? <RefreshCw size={11} className="animate-spin" /> : <Trash2 size={11} />}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
